// @/components/layout/navigation-widget.jsx
// Dynamic navigation widget with sidebar content
// Supports expanded (full) and collapsed (icon-only) modes

import React, { useMemo, useCallback, useState } from "react";
import { useLocation } from "@reach/router";
import { motion } from "motion/react";
import clsx from "clsx";

// Layout context
import { useLayout } from "./main";

// Theme configuration
import { getTheme } from "@/config/sidebar-theme";
import { HELP_URL } from "@/config/externalLinks";

// Hooks
import { usePermissions } from "@/hooks/permissions/usePermissions";
import { useContentSidebar } from "@/hooks/content/useContentSidebar";

// Services
import { logoutService } from "@/services/auth/logout";

// V2 reusable components
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  DropdownHeading,
  DropdownDivider,
  DropdownLabel,
  AvatarWithInfo,
  Avatar,
  NavItem,
  NavHeading,
} from "@/components/reusable";

// Badges
import CustomerRequestsBadge from "@/components/layout/CustomerRequestsBadge";
import SidebarLockedBadge from "@/components/reusable/SidebarLockedBadge";

// Icons
import OverviewIcon from "@/assets/icons/OverviewIcon";
import CarbonAccountingIcon from "@/assets/icons/CarbonAccountingIcon";
import SelfAssessmentIcon from "@/assets/icons/SelfAssessmentIcon";
import SupplyChainManagementIcon from "@/assets/icons/SupplyChainManagementIcon";
import CompanyProfileIcon from "@/assets/icons/ComanyProfileIcon";
import SupportIcon from "@/assets/icons/SupportIcon";
import SettingsIcon from "@/assets/icons/SettingsIcon";
import AccessManagementIcon from "@/assets/icons/AccessManagementIcon";
import PrivacyIcon from "@/assets/icons/PrivacyIcon";
import TermsIcon from "@/assets/icons/TermsIcon";
import SignOutIcon from "@/assets/icons/SignOutIcon";
import ChevronIcon from "@/assets/icons/ChevronIcon";

// Logos
import PlatformLogo from "@/assets/logos/PlatformLogo";
import CollapsedLogo from "@/assets/logos/CollapsedLogo";

import { BRAND_NAME } from "@/config/brand";
// ============================================================================
// ICONS
// ============================================================================

const ChevronUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-5"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const CollapseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="size-5 relative left-0.5"
  >
    <path
      fillRule="evenodd"
      d="M4.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L6.31 10l3.72-3.72a.75.75 0 1 0-1.06-1.06L4.72 9.47Zm9.25-4.25L9.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L11.31 10l3.72-3.72a.75.75 0 0 0-1.06-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const ExpandIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="size-5 rotate-180 relative left-0.5"
  >
    <path
      fillRule="evenodd"
      d="M4.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L6.31 10l3.72-3.72a.75.75 0 1 0-1.06-1.06L4.72 9.47Zm9.25-4.25L9.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L11.31 10l3.72-3.72a.75.75 0 0 0-1.06-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

// ============================================================================
// SIGN OUT BUTTON
// ============================================================================

const SignOutButton = ({ label }) => {
  const handleSignOut = useCallback(async () => {
    try {
      // Use centralized logout service - handles backend logout,
      // cross-tab notification, cache clearing, and navigation
      await logoutService.logout({ reason: 'user_initiated' });
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = "/logout/";
    }
  }, []);

  return (
    <DropdownItem onClick={handleSignOut}>
      <SignOutIcon />
      <DropdownLabel>{label}</DropdownLabel>
    </DropdownItem>
  );
};

// ============================================================================
// NAVIGATION WIDGET
// ============================================================================

const NavigationWidget = ({ className = "", forceExpanded = false, hideHeaderFooter = false }) => {
  const location = useLocation();
  const { isExpanded: contextIsExpanded, layout = "default", toggleSidebar } = useLayout();
  const permissions = usePermissions();
  const sidebarContent = useContentSidebar();

  const isExpanded = forceExpanded || contextIsExpanded;
  const theme = getTheme(layout);

  const [isHoveringToggleZone, setIsHoveringToggleZone] = useState(false);

  const isGuidedMode = useMemo(() => permissions.isGuidedMode(), [permissions]);
  const isLeadFirm = useMemo(() => {
    const result = permissions.isLeadFirm();
    if (process.env.NODE_ENV === 'development') {
      console.log('[NavigationWidget] isLeadFirm check:', {
        result,
        projectPrivileges: permissions.projectPrivileges,
        carbonAccounting: permissions.projectPrivileges?.CarbonAccounting
      });
    }
    return result;
  }, [permissions.projectPrivileges]);

  // Carbon Accounting path: lead firms go to /carbon-accounting-items/, others to /carbon-accounting/
  const carbonAccountingPath = useMemo(() => {
    return isLeadFirm ? '/carbon-accounting-items/' : '/carbon-accounting/';
  }, [isLeadFirm]);

  const isPathActive = useCallback((path) => location.pathname.startsWith(path), [location.pathname]);

  // Check if Carbon Accounting related paths are active (both regular and items)
  const isCarbonAccountingActive = useMemo(() => {
    return isPathActive("/carbon-accounting/") ||
      isPathActive("/carbon-accounting-items/") ||
      isPathActive("/prototypes/aca");
  }, [isPathActive]);

  return (
    <nav className={`group/sidebar relative flex flex-col h-full min-h-[600px] ${className}`}>

      {/* Header - Platform Logo with Dropdown */}
      {!hideHeaderFooter && (
        <div className={clsx(
          "shrink-0 p-4",
          isExpanded ? "border-b" : "",
          isExpanded && theme.widget.headerBorder
        )}>
          <Dropdown>
            <DropdownButton
              as="button"
              className={clsx(
                "flex items-center w-full rounded-xl transition-all duration-150",
                isExpanded ? "justify-between px-3 py-3" : "justify-center py-3",
                theme.widget.logoButtonHover
              )}
            >
              {isExpanded ? (
                <>
                  <PlatformLogo />
                  <span className={theme.widget.chevronColor}>
                    <ChevronIcon />
                  </span>
                </>
              ) : (
                <CollapsedLogo className="w-10" />
              )}
            </DropdownButton>
            <DropdownMenu className="min-w-64" anchor="bottom start">
              <DropdownItem href="https://trust.ctrl-s.io/" target="_blank" rel="noopener noreferrer">
                <PrivacyIcon />
                <DropdownLabel>{sidebarContent?.privacyPolicyLabel || "Trust Center"}</DropdownLabel>
              </DropdownItem>
              <DropdownItem href="https://ctrl-s.io/" target="_blank" rel="noopener noreferrer">
                <TermsIcon />
                <DropdownLabel>{sidebarContent?.aboutLabel || `About ${BRAND_NAME}`}</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      {/* Main Navigation */}
      <div className={clsx("flex-1 flex flex-col overflow-y-auto py-2", isExpanded ? "px-4" : "px-4")}>
        <div className="relative">
          {/* Floating active indicator */}
          {theme.nav.indicator.enabled &&
            isExpanded &&
            (isPathActive("/overview/") ||
              isPathActive("/self-assessment/") ||
              isCarbonAccountingActive ||
              (isPathActive("/supplier-engagement/") || isPathActive("/supply-chain-management/"))) && (
              <motion.div
                className={clsx(
                  "absolute",
                  theme.nav.indicator.base,
                  theme.nav.indicator.expandedLeft
                )}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  top: isPathActive("/overview/")
                    ? theme.nav.indicator.expandedPositions.overview
                    : isPathActive("/self-assessment/")
                      ? theme.nav.indicator.expandedPositions.selfAssessment
                      : isCarbonAccountingActive
                        ? theme.nav.indicator.expandedPositions.carbonAccounting
                        : (isPathActive("/supplier-engagement/") || isPathActive("/supply-chain-management/"))
                          ? theme.nav.indicator.expandedPositions.supplyChain
                          : theme.nav.indicator.expandedPositions.overview,
                }}
                transition={{
                  type: "spring",
                  damping: 25,
                  stiffness: 400,
                  mass: 0.5,
                }}
                style={{ height: theme.nav.indicator.expandedHeight }}
              />
            )}

          {/* Section: Report */}
          <div className={clsx("flex flex-col pt-4", isExpanded ? theme.nav.itemGap : theme.nav.itemGapCollapsed)}>
            {/* TODO: Re-enable after PO clarification */}
            {/* <NavHeading isExpanded={isExpanded}>Report</NavHeading> */}
            <NavItem
              to="/overview/"
              icon={<OverviewIcon />}
              label={sidebarContent?.overviewLabel || "Customer Requests"}
              isExpanded={isExpanded}
              active={isPathActive("/overview/")}
              badge={<CustomerRequestsBadge collapsed={!isExpanded} />}
            />
            <NavItem
              to={isGuidedMode ? "#" : "/self-assessment/"}
              icon={<SelfAssessmentIcon />}
              label={sidebarContent?.selfAssessmentLabel || "Self-Assessment"}
              isExpanded={isExpanded}
              active={isPathActive("/self-assessment/")}
              disabled={isGuidedMode}
              badge={isGuidedMode ? <SidebarLockedBadge collapsed={!isExpanded} /> : null}
            />
            <NavItem
              to={isGuidedMode ? "#" : carbonAccountingPath}
              icon={<CarbonAccountingIcon />}
              label={sidebarContent?.carbonAccountingLabel || "Carbon Accounting"}
              isExpanded={isExpanded}
              active={isCarbonAccountingActive}
              disabled={isGuidedMode}
              badge={isGuidedMode ? <SidebarLockedBadge collapsed={!isExpanded} /> : null}
            />
            {/* TODO: Re-enable as separate section with NavHeading after PO clarification */}
            {/* <NavHeading isExpanded={isExpanded}>Reduce</NavHeading> */}
            <NavItem
              to={isGuidedMode ? "#" : "/supplier-engagement/"}
              icon={<SupplyChainManagementIcon />}
              label={sidebarContent?.supplyChainManagementLabel || "Supplier Engagement"}
              isExpanded={isExpanded}
              active={(isPathActive("/supplier-engagement/") || isPathActive("/supply-chain-management/"))}
              disabled={isGuidedMode}
              badge={isGuidedMode ? <SidebarLockedBadge collapsed={!isExpanded} /> : null}
            />
          </div>
        </div>

        {/* Toggle Zone */}
        {!forceExpanded ? (
          <div
            className="flex-1 relative cursor-pointer group/toggle"
            onClick={toggleSidebar}
            onMouseEnter={() => setIsHoveringToggleZone(true)}
            onMouseLeave={() => setIsHoveringToggleZone(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleSidebar()}
            aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <div
              className={clsx(
                "absolute -right-4 top-1/2",
                "p-1.5 rounded-l-2xl",
                "transition-opacity duration-150 ease-out",
                theme.widget.collapseButton,
                isHoveringToggleZone ? "opacity-100" : "opacity-0"
              )}
            >
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </div>
          </div>
        ) : (
          <div className="flex-1" aria-hidden="true" />
        )}

        {/* Section: Other (Help) */}
        <div className={clsx("flex flex-col", isExpanded ? clsx("pb-2", theme.nav.itemGap) : theme.nav.itemGapCollapsed)}>
          <NavItem
            to={HELP_URL}
            icon={<SupportIcon />}
            label={sidebarContent?.helpLabel || "Help"}
            isExpanded={isExpanded}
            external
          />
        </div>
      </div>

      {/* Footer - User Avatar with Dropdown */}
      {!hideHeaderFooter && (
        <div className={clsx(
          "shrink-0",
          isExpanded ? "px-4 border-t pt-6 pb-6" : "px-3 pb-3",
          isExpanded && theme.widget.footerBorder
        )}>
          <Dropdown>
            <DropdownButton
              as="button"
              className={clsx(
                "flex items-center w-full rounded-xl transition-all duration-150",
                isExpanded ? "justify-between p-3" : "justify-center py-3",
                theme.widget.userButtonHover
              )}
            >
              {isExpanded ? (
                <>
                  <AvatarWithInfo size="md" />
                  <span className={theme.widget.chevronColor}>
                    <ChevronUpIcon />
                  </span>
                </>
              ) : (
                <Avatar size="md" />
              )}
            </DropdownButton>
            <DropdownMenu className="min-w-64" anchor="top start">
              <DropdownSection>
                <DropdownHeading>{sidebarContent?.accountLabel || "Account"}</DropdownHeading>
              </DropdownSection>
              <DropdownItem to="/account/settings/" current={isPathActive("/account/settings/")}>
                <SettingsIcon />
                <DropdownLabel>{sidebarContent?.settingsLabel || "Settings"}</DropdownLabel>
              </DropdownItem>

              <DropdownDivider />

              <DropdownSection>
                <DropdownHeading>{sidebarContent?.companySettingsLabel || "Company Settings"}</DropdownHeading>
              </DropdownSection>
              <DropdownItem to="/account/company-profile/" current={isPathActive("/account/company-profile/")}>
                <CompanyProfileIcon />
                <DropdownLabel>{sidebarContent?.companyProfileLabel || "Company Profile"}</DropdownLabel>
              </DropdownItem>
              <DropdownItem to="/account/users/" current={isPathActive("/account/users/")}>
                <AccessManagementIcon />
                <DropdownLabel>{sidebarContent?.userManagementLabel || "User Management"}</DropdownLabel>
              </DropdownItem>

              <DropdownDivider />
              <SignOutButton label={sidebarContent?.signOutLabel || "Sign out"} />
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
    </nav>
  );
};

export default NavigationWidget;
